import React from 'react';
import { Sparkles } from 'lucide-react';

/**
 * Reusable hero header for the 3 dashboards (Admin, Tenant, Owner).
 *
 * Props :
 *  - accentColor  : hex used for the accent strip and metric tints
 *  - role         : short role badge text (e.g. 'AGENCE', 'LOCATAIRE')
 *  - greeting     : main heading (e.g. 'Bonjour, Mariam')
 *  - subtitle     : descriptive paragraph
 *  - metrics      : array of { label, value, icon: LucideIcon, accent?: boolean }
 *  - rightSlot    : optional ReactNode rendered top-right (CTA, date selector...)
 */
const DashboardHero = ({
  accentColor = '#D4A745',
  role = '',
  greeting = 'Bonjour',
  subtitle = '',
  metrics = [],
  rightSlot,
}) => {
  return (
    <div
      className="relative overflow-hidden rounded-3xl text-white"
      style={{
        background: 'linear-gradient(135deg, #142D3A 0%, #1B3A4B 50%, #0F2330 100%)',
        boxShadow: '0 24px 48px -16px rgba(15, 35, 48, 0.35), 0 8px 16px -8px rgba(15, 35, 48, 0.2)',
      }}
      data-testid="dashboard-hero"
    >
      {/* Decorative ambient gradients */}
      <div
        className="absolute inset-0 opacity-40 pointer-events-none"
        style={{
          background: `radial-gradient(circle at 82% 8%, ${accentColor}60, transparent 38%), radial-gradient(circle at 12% 95%, ${accentColor}30, transparent 45%)`,
        }}
      />

      {/* Subtle grid texture */}
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage: `linear-gradient(${accentColor} 1px, transparent 1px), linear-gradient(90deg, ${accentColor} 1px, transparent 1px)`,
          backgroundSize: '44px 44px',
        }}
      />

      {/* Concentric decoration */}
      <svg
        className="absolute -right-20 -bottom-24 w-[340px] h-[340px] opacity-[0.07] pointer-events-none"
        viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg"
      >
        <circle cx="100" cy="100" r="92" fill="none" stroke={accentColor} strokeWidth="0.5" />
        <circle cx="100" cy="100" r="68" fill="none" stroke={accentColor} strokeWidth="0.5" />
        <circle cx="100" cy="100" r="44" fill="none" stroke={accentColor} strokeWidth="0.5" />
        <circle cx="100" cy="100" r="20" fill={accentColor} fillOpacity="0.15" />
      </svg>

      {/* Top-left glint */}
      <div
        className="absolute -top-32 -left-32 w-[420px] h-[420px] rounded-full pointer-events-none"
        style={{
          background: `radial-gradient(circle, ${accentColor}1f 0%, transparent 60%)`,
          filter: 'blur(40px)',
        }}
      />

      <div className="relative z-10 p-6 sm:p-8 lg:p-10">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="min-w-0">
            {role && (
              <span
                className="inline-flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.2em] px-3 py-1.5 rounded-full backdrop-blur-sm"
                style={{
                  backgroundColor: `${accentColor}1f`,
                  color: accentColor,
                  border: `1px solid ${accentColor}55`,
                }}
              >
                <Sparkles className="w-3 h-3" />
                {role}
              </span>
            )}
            <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05]"
                style={{ fontFamily: "'Outfit', sans-serif" }}>
              {greeting}
            </h1>
            {subtitle && (
              <p className="mt-3 text-white/65 text-sm sm:text-base leading-relaxed max-w-2xl">
                {subtitle}
              </p>
            )}
          </div>
          {rightSlot && <div className="flex items-center gap-2">{rightSlot}</div>}
        </div>

        {metrics.length > 0 && (
          <div className="mt-7 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {metrics.map((m, i) => {
              const Icon = m.icon;
              const tint = m.accent ? accentColor : '#FFFFFF';
              return (
                <div
                  key={i}
                  className="group relative backdrop-blur-xl bg-white/[0.06] border border-white/10 rounded-2xl p-4 hover:bg-white/[0.10] hover:border-white/20 transition-all duration-300 stagger-item"
                  style={{ animationDelay: `${i * 60}ms` }}
                  data-testid={`hero-metric-${i}`}
                >
                  {/* hover shine */}
                  <div
                    className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                    style={{
                      background: `linear-gradient(135deg, ${accentColor}10 0%, transparent 60%)`,
                    }}
                  />
                  <div className="relative flex items-center justify-between">
                    <span className="text-[10px] uppercase tracking-[0.14em] text-white/55 font-semibold">
                      {m.label}
                    </span>
                    {Icon && (
                      <div
                        className="w-7 h-7 rounded-lg flex items-center justify-center"
                        style={{
                          backgroundColor: m.accent ? `${accentColor}22` : 'rgba(255,255,255,0.06)',
                        }}
                      >
                        <Icon className="w-3.5 h-3.5" style={{ color: tint }} />
                      </div>
                    )}
                  </div>
                  <p
                    className="relative mt-2 font-bold text-xl truncate tracking-tight"
                    style={{ color: tint, fontFamily: "'Outfit', sans-serif" }}
                  >
                    {m.value}
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Bottom accent line */}
      <div
        className="absolute bottom-0 left-0 right-0 h-[2px] pointer-events-none"
        style={{
          background: `linear-gradient(90deg, transparent 0%, ${accentColor}80 30%, ${accentColor} 50%, ${accentColor}80 70%, transparent 100%)`,
        }}
      />
    </div>
  );
};

export default DashboardHero;
